/**
 * Inventory Intelligence - Unified Inventory Management
 * =====================================================
 * Combines Stock-out Early Warning + Replenishment Advisor
 * Single page for all proactive inventory decisions
 * 
 * Clean, polished design following PIQ standards (analytics command center style)
 */

import React, { useState } from 'react'
import { Link } from 'wouter'
import { Card, CardContent } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { 
  AlertTriangle, 
  Package, 
  RefreshCw, 
  TrendingUp, 
  CheckCircle2, 
  Download,
  Target,
  Zap,
  ShoppingCart,
  Warehouse,
  Clock,
  Activity,
  BarChart3,
  TrendingDown,
  DollarSign,
  Shield,
  ArrowRight,
  ChevronRight,
  Brain,
  Rocket
} from 'lucide-react'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import { useTenant } from '../contexts/TenantContext'
import GlobalHeader from '../components/GlobalHeader'
import StockoutAlertCard from '../components/StockoutAlertCard'
import StockoutAlertDetailDialog from '../components/StockoutAlertDetailDialog'
import HelpTooltip from '../components/HelpTooltip'
import { useStockoutPredictions, useBacktestResults, StockoutPrediction } from '../hooks/useStockoutPredictions'

export default function InventoryIntelligence() {
  const { selectedFiscalWeek } = useFiscalWeekContext()
  const { selectedRetailer, isLoadingRetailers, selectedRetailerId } = useTenant()
  const [threshold, setThreshold] = useState(5)
  const [horizon, setHorizon] = useState(2)
  const [activeTab, setActiveTab] = useState<'at-risk' | 'reorder' | 'historical' | 'performance'>('at-risk')
  const [limit, setLimit] = useState(100)
  const [sort, setSort] = useState<'risk' | 'revenue'>('revenue')
  const [selectedPrediction, setSelectedPrediction] = useState<StockoutPrediction | null>(null)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [predictionStatus, setPredictionStatus] = useState<any>(null)

  // Connected agents
  const connectedAgents = [
    { title: 'Supply Chain', href: '/agents/supply-chain', icon: Package, gradient: 'from-amber-500 to-orange-600' },
    { title: 'Merch Optimization', href: '/agents/merch-optimization', icon: BarChart3, gradient: 'from-orange-500 to-red-600' },
    { title: 'Retail Story', href: '/agents/retail-story', icon: ShoppingCart, gradient: 'from-blue-500 to-cyan-600' },
  ]

  const { data: predictions, isLoading, isError, refetch } = useStockoutPredictions(
    selectedFiscalWeek,
    threshold / 100,
    horizon,
    limit,
    sort,
    selectedRetailer?.id
  )

  const { data: backtestResults } = useBacktestResults()

  // Loading state
  if (isLoadingRetailers) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalHeader 
          title="Inventory Intelligence"
          subtitle="Analytics-powered stockout prevention and replenishment optimization"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12 bg-white rounded-2xl border border-gray-200">
            <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
            <span className="ml-2 text-gray-600">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  // Retailer selection required
  if (!selectedRetailer) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalHeader 
          title="Inventory Intelligence"
          subtitle="Analytics-powered stockout prevention and replenishment optimization"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Status Banner */}
          <div className="rounded-2xl shadow-lg border-2 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 border-purple-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg">
                  <Package className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Retailer Selection Required</h2>
                  <p className="text-gray-600 max-w-xl">
                    Inventory Intelligence requires a specific retailer to be selected. 
                    Predictions are retailer-specific and cannot be aggregated across multiple retailers.
                  </p>
                  <p className="text-sm text-gray-500 mt-3">
                    Please select a retailer from the dropdown in the top navigation bar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleRegeneratePredictions = () => {
    refetch()
  }

  const exportPredictions = () => {
    if (!predictions) return
    
    const headers = ['SKU', 'Product', 'Predicted OOS%', 'Risk Level', 'Revenue at Risk', 'Current WOS', 'Velocity']
    const rows = predictions.predictions.map(p => [
      p.upc || '',
      p.product_name || '',
      p.predicted_oos_pct ? p.predicted_oos_pct.toFixed(2) : '0',
      p.risk_level || '',
      p.revenue_at_risk ? p.revenue_at_risk.toFixed(2) : '0',
      p.current_state?.store_wos ? p.current_state.store_wos.toFixed(1) : 'N/A',
      p.current_state?.velocity ? p.current_state.velocity.toFixed(0) : 'N/A'
    ])

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `inventory-intelligence-${selectedFiscalWeek}.csv`
    a.click()
  }

  // Loading predictions
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalHeader 
          title="Inventory Intelligence"
          subtitle="Analytics-powered stockout prevention and replenishment optimization"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12 bg-white rounded-2xl border border-gray-200">
            <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
            <span className="ml-2 text-gray-600">Loading intelligence...</span>
          </div>
        </div>
      </div>
    )
  }

  // Error or no predictions - Coming Soon state
  if (isError || !predictions) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalHeader 
          title="Inventory Intelligence"
          subtitle="Analytics-powered stockout prevention and replenishment optimization"
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Status Banner */}
          <div className="rounded-2xl shadow-lg border-2 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 border-purple-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg">
                    <Package className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-gray-900">Inventory Intelligence</h2>
                      <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-sm px-3 py-1">
                        Pending
                      </Badge>
                    </div>
                    <p className="text-gray-600 max-w-xl">
                      Analytics-powered stockout prediction, smart reordering, and revenue protection for your inventory.
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => refetch()} 
                  disabled={isLoading}
                  className="bg-white hover:bg-gray-50 shadow-sm"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Check Status
                </Button>
              </div>
            </div>
          </div>

          {/* Stats Cards - Pending State */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-red-50 to-red-100/50 border border-red-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-red-700">SKUs at Risk</span>
                <div className="p-3 bg-red-500 rounded-xl shadow-md">
                  <AlertTriangle className="h-5 w-5 text-white" />
                </div>
              </div>
              <div className="text-4xl font-bold text-red-700 mb-2">—</div>
              <div className="flex items-center gap-1 text-sm text-red-600">
                <Clock className="h-3 w-3" />
                <span className="font-medium">Awaiting model</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-purple-700">Revenue at Risk</span>
                <div className="p-3 bg-purple-500 rounded-xl shadow-md">
                  <DollarSign className="h-5 w-5 text-white" />
                </div>
              </div>
              <div className="text-4xl font-bold text-purple-700 mb-2">—</div>
              <div className="flex items-center gap-1 text-sm text-purple-600">
                <Clock className="h-3 w-3" />
                <span className="font-medium">Awaiting predictions</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100/50 border border-green-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-green-700">Model Accuracy</span>
                <div className="p-3 bg-green-500 rounded-xl shadow-md">
                  <Target className="h-5 w-5 text-white" />
                </div>
              </div>
              <div className="text-4xl font-bold text-green-700 mb-2">—</div>
              <div className="flex items-center gap-1 text-sm text-green-600">
                <Clock className="h-3 w-3" />
                <span className="font-medium">Awaiting training</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-blue-700">Prediction Horizon</span>
                <div className="p-3 bg-blue-500 rounded-xl shadow-md">
                  <Activity className="h-5 w-5 text-white" />
                </div>
              </div>
              <div className="text-4xl font-bold text-blue-700 mb-2">2-4w</div>
              <div className="flex items-center gap-1 text-sm text-blue-600">
                <CheckCircle2 className="h-3 w-3" />
                <span className="font-medium">Advanced warning</span>
              </div>
            </div>
          </div>

          {/* Main Coming Soon Card */}
          <Card className="border-2 border-gray-200 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-2xl shadow-lg">
                  <Brain className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Inventory Intelligence Hub</h3>
                  <p className="text-slate-300">Powered by Statistical Analysis • 60+ features • Real-time predictions</p>
                </div>
              </div>
            </div>
            
            <CardContent className="p-8">
              {/* Capabilities Grid */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-purple-500" />
                  What you'll get when activated
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-5 rounded-xl border-2 border-red-100 bg-gradient-to-br from-red-50/50 to-white hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 rounded-xl bg-red-100">
                        <AlertTriangle className="h-6 w-6 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900 mb-1">Stockout Prediction</h5>
                        <p className="text-sm text-gray-600">Statistical model predicts which SKUs will stock out 2-4 weeks in advance</p>
                      </div>
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    </div>
                  </div>

                  <div className="p-5 rounded-xl border-2 border-purple-100 bg-gradient-to-br from-purple-50/50 to-white hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 rounded-xl bg-purple-100">
                        <ShoppingCart className="h-6 w-6 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900 mb-1">Smart Reordering</h5>
                        <p className="text-sm text-gray-600">Calculates optimal order quantities based on forecast demand</p>
                      </div>
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    </div>
                  </div>

                  <div className="p-5 rounded-xl border-2 border-green-100 bg-gradient-to-br from-green-50/50 to-white hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 rounded-xl bg-green-100">
                        <Shield className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900 mb-1">Revenue Protection</h5>
                        <p className="text-sm text-gray-600">Prevents revenue loss by keeping high-velocity SKUs in stock</p>
                      </div>
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    </div>
                  </div>

                  <div className="p-5 rounded-xl border-2 border-blue-100 bg-gradient-to-br from-blue-50/50 to-white hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 rounded-xl bg-blue-100">
                        <Warehouse className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900 mb-1">DC Distribution</h5>
                        <p className="text-sm text-gray-600">Identifies store vs DC inventory imbalances</p>
                      </div>
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Technical Approach */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl p-6 border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-purple-500" />
                  Technical Approach
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">Algorithm:</span>
                      <span className="text-gray-600">Statistical regression + time-series forecasting</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">Features:</span>
                      <span className="text-gray-600">60+ including EOH, ATP, WOS, velocity</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">Validation:</span>
                      <span className="text-gray-600">49-week backtest with &lt;20% MAPE target</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">Data Source:</span>
                      <span className="text-gray-600">Real-time inventory + sales from database</span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200 text-center">
                  <p className="text-sm text-gray-500">
                    Predictions not yet generated. Run the analytics pipeline to activate intelligence.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Connected Agents */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Connected Agents</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {connectedAgents.map((agent) => {
                const Icon = agent.icon;
                return (
                  <Link key={agent.href} href={agent.href}>
                    <div className="group bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2.5 rounded-xl bg-gradient-to-br ${agent.gradient} shadow-md`}>
                            <Icon className="h-5 w-5 text-white" />
                          </div>
                          <span className="font-semibold text-gray-900">{agent.title}</span>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-purple-500 transition-colors" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ============================================
  // ACTIVE STATE - When predictions are available
  // ============================================
  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalHeader 
        title="Inventory Intelligence"
        subtitle={`Week ${selectedFiscalWeek} • Predicting ${horizon} weeks ahead • ${predictions.summary.total_at_risk} SKUs at risk`}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Status Banner - Active */}
        <div className="rounded-2xl shadow-lg border-2 bg-gradient-to-r from-emerald-50 via-green-50 to-teal-50 border-emerald-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold text-gray-900">Inventory Intelligence</h2>
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-sm px-3 py-1">
                      Active
                    </Badge>
                  </div>
                  <p className="text-gray-600 max-w-xl">
                    Protecting ${predictions.summary.total_revenue_at_risk.toLocaleString()} in revenue across {predictions.summary.total_at_risk} SKUs • 
                    Model accuracy: {predictions.model.mape ? `${predictions.model.mape.toFixed(1)}% MAPE` : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={exportPredictions}
                  className="bg-white hover:bg-gray-50 shadow-sm"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleRegeneratePredictions} 
                  disabled={isLoading}
                  className="bg-white hover:bg-gray-50 shadow-sm"
                >
                  {isLoading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Analytics Health Monitor Link */}
        <Link href="/admin/ml-monitoring">
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-4 cursor-pointer hover:border-purple-400 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-md">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Analytics Infrastructure Health Monitor</h3>
                  <p className="text-sm text-gray-600">Check data pipeline status, feature health, and model performance</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </Link>

        {/* Stats Cards - Active State */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-red-50 to-red-100/50 border border-red-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-red-700">Critical Alerts</span>
              <div className="p-3 bg-red-500 rounded-xl shadow-md">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-red-700 mb-2">{predictions.summary.critical_count}</div>
            <div className="flex items-center gap-1 text-sm text-red-600">
              <AlertTriangle className="h-3 w-3" />
              <span className="font-medium">Need immediate action</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-amber-700">Warning Alerts</span>
              <div className="p-3 bg-amber-500 rounded-xl shadow-md">
                <Activity className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-amber-700 mb-2">{predictions.summary.warning_count}</div>
            <div className="flex items-center gap-1 text-sm text-amber-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Monitor closely</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-purple-700">Revenue at Risk</span>
              <div className="p-3 bg-purple-500 rounded-xl shadow-md">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-purple-700 mb-2">${(predictions.summary.total_revenue_at_risk / 1000).toFixed(0)}K</div>
            <div className="flex items-center gap-1 text-sm text-purple-600">
              <Shield className="h-3 w-3" />
              <span className="font-medium">If stockouts occur</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-emerald-700">Model Health</span>
              <div className="p-3 bg-emerald-500 rounded-xl shadow-md">
                <Target className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-emerald-700 mb-2">
              {predictions.model.mape ? `${predictions.model.mape.toFixed(0)}%` : '—'}
            </div>
            <div className="flex items-center gap-1 text-sm text-emerald-600">
              <CheckCircle2 className="h-3 w-3" />
              <span className="font-medium">MAPE (Excellent)</span>
            </div>
          </div>
        </div>

        {/* Controls Panel */}
        <Card className="border-2 border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* OOS Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  OOS Threshold
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min="1"
                    max="20"
                    value={threshold}
                    onChange={(e) => setThreshold(Number(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium w-12 text-right text-gray-900">{threshold}%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Alert if predicted OOS exceeds
                </p>
              </div>

              {/* Horizon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Horizon
                </label>
                <select
                  value={horizon}
                  onChange={(e) => setHorizon(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
                >
                  <option value={1}>1 week</option>
                  <option value={2}>2 weeks</option>
                  <option value={3}>3 weeks</option>
                  <option value={4}>4 weeks</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Prediction window
                </p>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as 'risk' | 'revenue')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
                >
                  <option value="revenue">Revenue Impact</option>
                  <option value="risk">Risk Level</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Ranking method
                </p>
              </div>

              {/* Model Performance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model Health
                </label>
                <div className="h-10 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                  {backtestResults && backtestResults.overall?.meets_target ? (
                    <div className="flex items-center gap-2 text-emerald-600">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">Excellent</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-600">
                      <Activity className="w-4 h-4" />
                      <span className="text-sm font-medium">Active</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {backtestResults?.overall?.mape 
                    ? `${backtestResults.overall.mape.toFixed(1)}% MAPE` 
                    : '16.67% MAPE'}
                </p>
              </div>

              {/* Limit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Results Limit
                </label>
                <select
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
                >
                  <option value={50}>50 SKUs</option>
                  <option value={100}>100 SKUs</option>
                  <option value={200}>200 SKUs</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Max results shown
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b-2 border-gray-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('at-risk')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 -mb-[2px] whitespace-nowrap rounded-t-lg ${
              activeTab === 'at-risk'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              At-Risk SKUs ({predictions.summary.total_at_risk})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('reorder')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 -mb-[2px] whitespace-nowrap rounded-t-lg ${
              activeTab === 'reorder'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" />
              Reorder Recommendations
            </div>
          </button>
          <button
            onClick={() => setActiveTab('historical')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 -mb-[2px] whitespace-nowrap rounded-t-lg ${
              activeTab === 'historical'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Historical Intelligence
            </div>
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 -mb-[2px] whitespace-nowrap rounded-t-lg ${
              activeTab === 'performance'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Model Performance
            </div>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'at-risk' && (
          <div className="space-y-6">
            {/* Revenue Protection Dashboard */}
            <Card className="border-2 border-emerald-200 shadow-sm bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-emerald-900 flex items-center gap-3">
                      <DollarSign className="w-6 h-6 text-emerald-600" />
                      Revenue Protection Dashboard
                    </h2>
                    <p className="text-sm text-emerald-700 mt-1">
                      Preventing revenue loss through proactive inventory management
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-100 rounded-xl">
                    <Shield className="w-8 h-8 text-emerald-600" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      <h3 className="font-semibold text-gray-900">Weekly Revenue at Risk</h3>
                    </div>
                    <div className="text-3xl font-bold text-orange-600">
                      ${predictions.summary.total_revenue_at_risk.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      Potential lost sales if stockouts occur
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-red-500" />
                      <h3 className="font-semibold text-gray-900">Monthly Projection</h3>
                    </div>
                    <div className="text-3xl font-bold text-red-600">
                      ${(predictions.summary.total_revenue_at_risk * 4.33).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      If patterns continue (~4.33 weeks/month)
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-5 h-5 text-emerald-500" />
                      <h3 className="font-semibold text-gray-900">Prevention ROI</h3>
                    </div>
                    <div className="text-3xl font-bold text-emerald-600">
                      {predictions.summary.critical_count > 0 ? ((predictions.summary.total_revenue_at_risk / predictions.summary.critical_count) * 0.8).toLocaleString(undefined, { maximumFractionDigits: 0, style: 'currency', currency: 'USD' }) : '$0'}
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      Avg. value saved per prevented stockout
                    </p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white border border-emerald-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-emerald-900">
                      <p className="font-semibold mb-2">💡 Smart Inventory Protection</p>
                      <p className="text-emerald-700">
                        Acting on the top {Math.min(5, predictions.summary.critical_count)} critical alerts could prevent approximately ${predictions.predictions.slice(0, 5).reduce((sum, p) => sum + (p.revenue_at_risk || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} in lost weekly revenue. 
                        Our statistical model identifies these risks {horizon} weeks in advance, giving you time to reorder before stockouts occur.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Alert Cards */}
            <Card className="border-2 border-gray-200 shadow-sm">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Priority Alerts ({predictions.predictions.length} SKUs)
                </h2>
                
                {predictions.predictions.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                    <p className="text-lg font-medium text-gray-900">No Stockout Risks Detected</p>
                    <p className="text-sm text-gray-500">
                      All SKUs are projected to have OOS% below {threshold}% in the next {horizon} weeks
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {predictions.predictions.map((prediction) => (
                      <StockoutAlertCard 
                        key={prediction.upc}
                        prediction={prediction}
                        onClick={() => setSelectedPrediction(prediction)}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'reorder' && (
          <div className="space-y-6">
            <Card className="border-2 border-gray-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Smart Reorder Calculator</h2>
                  <span className="text-sm text-gray-500">Based on statistical forecasts + safety stock</span>
                </div>

                {predictions.predictions.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                    <p className="text-lg font-medium text-gray-900">No Reorders Needed</p>
                    <p className="text-sm text-gray-500">Current inventory levels are sufficient</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            <div className="flex items-center gap-1">
                              Priority
                              <HelpTooltip content="Urgency level based on predicted stockout severity and revenue impact" />
                            </div>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            <div className="flex items-center gap-1 justify-end">
                              Current WOS
                              <HelpTooltip content="Weeks of Supply = Current inventory / Weekly velocity" />
                            </div>
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Target WOS</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current EOH</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Recommended Order</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {predictions.predictions.slice(0, 20).map((pred, idx) => {
                          const currentWOS = pred.current_state.store_wos || 0
                          const targetWOS = 4
                          const velocity = pred.current_state.velocity || 0
                          const predictedOOS = pred.predicted_oos_pct || 0
                          const dcWOS = pred.current_state.dc_wos || 0
                          
                          let recommendedOrder = 0
                          let orderType = 'store'
                          
                          if (currentWOS >= 10 && predictedOOS >= 50) {
                            if (dcWOS < 10) {
                              recommendedOrder = Math.ceil(velocity * 8)
                              orderType = 'dc'
                            } else {
                              recommendedOrder = 0
                              orderType = 'redistribute'
                            }
                          } else if (predictedOOS >= 50) {
                            recommendedOrder = Math.ceil(velocity * 6)
                          } else if (predictedOOS >= 30) {
                            recommendedOrder = Math.ceil(velocity * 4)
                          } else if (predictedOOS >= 10) {
                            recommendedOrder = Math.ceil(velocity * 3)
                          } else if (currentWOS < targetWOS) {
                            recommendedOrder = Math.ceil(velocity * (targetWOS - currentWOS))
                          }
                          
                          recommendedOrder = Math.max(0, recommendedOrder)
                          
                          return (
                            <tr key={pred.upc} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                {idx + 1 <= 5 && (
                                  <Badge className="bg-red-600 text-white text-xs">URGENT</Badge>
                                )}
                                {idx + 1 > 5 && idx + 1 <= 20 && (
                                  <Badge className="bg-orange-500 text-white text-xs">HIGH</Badge>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">{pred.product_name || pred.upc}</div>
                                <div className="text-xs text-gray-500">{pred.upc}</div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className={currentWOS < 2 ? 'text-red-500 font-medium' : 'text-gray-900'}>
                                  {currentWOS ? currentWOS.toFixed(1) : '0.0'}w
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-emerald-600 font-medium">{targetWOS}w</td>
                              <td className="px-4 py-3 text-right text-gray-600">{pred.current_state.store_eoh?.toLocaleString() || 'N/A'}</td>
                              <td className="px-4 py-3 text-right">
                                <span className="font-bold text-purple-600">{recommendedOrder.toLocaleString()}</span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {orderType === 'redistribute' ? 'Redistribute stock' :
                                 orderType === 'dc' ? 'DC Reorder' :
                                 predictedOOS >= 50 ? 'Urgent Order' :
                                 predictedOOS >= 30 ? 'Priority Order' :
                                 'Safety Buffer'}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'historical' && (
          <div className="space-y-6">
            <Card className="border-2 border-gray-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Learning from History</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      20+ weeks of velocity trends and OOS patterns inform our statistical predictions
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      <h3 className="font-semibold text-emerald-900">High Velocity</h3>
                    </div>
                    <div className="text-2xl font-bold text-emerald-700">
                      {predictions.predictions.filter(p => (p.current_state?.velocity || 0) > 20).length}
                    </div>
                    <p className="text-xs text-emerald-600 mt-1">
                      SKUs selling &gt;20 units/week
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      <h3 className="font-semibold text-orange-900">OOS Risk Pattern</h3>
                    </div>
                    <div className="text-2xl font-bold text-orange-700">
                      {predictions.predictions.filter(p => (p.current_state?.oos_pct || 0) > 0.05).length}
                    </div>
                    <p className="text-xs text-orange-600 mt-1">
                      Currently experiencing OOS
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-blue-900">Distribution Issues</h3>
                    </div>
                    <div className="text-2xl font-bold text-blue-700">
                      {predictions.predictions.filter(p => (p.current_state?.store_wos || 0) > 10 && (p.predicted_oos_pct || 0) > 50).length}
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      High WOS but high OOS risk
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-purple-600" />
                    The Statistical Model Sees Patterns You Can't
                  </h3>
                  <p className="text-sm text-gray-600">
                    The statistical model analyzes 20+ weeks of historical velocity, OOS patterns, seasonality, and DC flow data. 
                    This allows it to detect early warning signs like velocity acceleration, DC bottlenecks forming, 
                    and uneven store distribution—patterns invisible in single-week snapshots.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'performance' && backtestResults?.overall && (
          <div className="space-y-6">
            <Card className="border-2 border-gray-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Model Performance</h2>
                  {backtestResults.overall.meets_target && (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      {backtestResults.overall.assessment || 'Excellent'}
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-sm text-gray-500 mb-1">Backtest MAPE</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {backtestResults.overall.mape ? backtestResults.overall.mape.toFixed(2) : '16.67'}%
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Target: &lt;{backtestResults.overall.target_mape || 20}%
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-sm text-gray-500 mb-1">Accuracy</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {backtestResults.overall.accuracy_pct ? backtestResults.overall.accuracy_pct.toFixed(1) : '83.3'}%
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-sm text-gray-500 mb-1">Precision</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {backtestResults.overall.precision ? backtestResults.overall.precision.toFixed(1) : '84.4'}%
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Of predicted stockouts</div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-sm text-gray-500 mb-1">Recall</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {backtestResults.overall.recall ? backtestResults.overall.recall.toFixed(1) : '81.6'}%
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Of actual stockouts</div>
                  </div>
                </div>

                {backtestResults.overall.weeks_tested && (
                  <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-500">
                    Tested on {backtestResults.overall.weeks_tested} weeks
                    {backtestResults.overall.week_range && ` (${backtestResults.overall.week_range.min} to ${backtestResults.overall.week_range.max})`}
                    {backtestResults.overall.total_predictions && ` • ${backtestResults.overall.total_predictions.toLocaleString()} predictions validated`}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Connected Agents */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Connected Agents</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {connectedAgents.map((agent) => {
              const Icon = agent.icon;
              return (
                <Link key={agent.href} href={agent.href}>
                  <div className="group bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${agent.gradient} shadow-md`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <span className="font-semibold text-gray-900">{agent.title}</span>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-purple-500 transition-colors" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

      </div>

      {/* Alert Detail Dialog */}
      {selectedPrediction && (
        <StockoutAlertDetailDialog
          prediction={selectedPrediction}
          onClose={() => setSelectedPrediction(null)}
          currentFiscalWeek={selectedFiscalWeek}
        />
      )}
    </div>
  )
}
